import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  Chip,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import FlexService from '../../services/FlexService';
import { enterAdvanceProps } from '../../utils/enterAdvance';
import { formatDateOnly } from '../../utils/dateOnly';

const CountEntryPanel = ({
  rowData,
  config,
  colors,
  onSave,
  onCancel,
}) => {
  // Task info
  const [taskInfo, setTaskInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Location verification
  const [locationScan, setLocationScan] = useState('');
  const [locationVerified, setLocationVerified] = useState(false);
  const [locationError, setLocationError] = useState('');

  // Entry form
  const [formValues, setFormValues] = useState({
    palletId: '',
    caseId: '',
    itemId: '',
    quantityCounted: '',
    uom: '',
    inventoryStatus: '',
    lotCode: '',
    expirationDate: '',
    ownerId: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [addingEntry, setAddingEntry] = useState(false);

  // Entries grid
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);

  // FH-154 #3: lets a counter submit a deliberate zero-count when the
  // location is genuinely empty, instead of always being blocked.
  const [confirmedEmpty, setConfirmedEmpty] = useState(false);

  // Status options
  const [statusOptions, setStatusOptions] = useState([]);

  // Submitting
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const countTaskId = rowData?.countTaskId;
  const expectedLocationId = rowData?.locationId;

  // Load task info and entries on mount
  useEffect(() => {
    const loadTask = async () => {
      setLoading(true);
      try {
        if (config.taskLoadScript) {
          const res = await FlexService.executeScript({
            scriptName: config.taskLoadScript,
            context: { _payload: { countTaskId } },
          });
          if (res.success && res.context?.result) {
            const task = res.context.result;
            setTaskInfo(task);
            if (task.countData && Array.isArray(task.countData)) {
              setEntries(task.countData);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load task:', err);
      } finally {
        setLoading(false);
      }
    };
    if (countTaskId) loadTask();
  }, [countTaskId, config.taskLoadScript]);

  // Load inventory status options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const res = await FlexService.executeScript({
          scriptName: 'inventory_status_options',
          context: {},
        });
        if (res.success && res.context?.result) {
          const opts = res.context.result;
          setStatusOptions(Array.isArray(opts) ? opts : []);
        }
      } catch (err) {
        console.error('Failed to load status options:', err);
      }
    };
    loadOptions();
  }, []);

  // Reload entries from backend
  const reloadEntries = useCallback(async () => {
    if (!config.dataLoadScript) return;
    try {
      const res = await FlexService.executeScript({
        scriptName: config.dataLoadScript,
        context: { _queryParams: { countTaskId } },
      });
      if (res.success && res.context?.result) {
        setEntries(Array.isArray(res.context.result) ? res.context.result : []);
      }
    } catch (err) {
      console.error('Failed to reload entries:', err);
    }
  }, [config.dataLoadScript, countTaskId]);

  // Location verification handler
  const handleLocationVerify = useCallback(() => {
    const scanned = locationScan.trim().toUpperCase();
    const expected = (expectedLocationId || '').toUpperCase();

    if (!scanned) {
      setLocationError('Please scan or enter the location ID');
      return;
    }

    if (scanned !== expected) {
      setLocationError(`Scanned "${locationScan.trim()}" does not match assigned location "${expectedLocationId}". Please scan the correct location.`);
      setLocationVerified(false);
      return;
    }

    setLocationVerified(true);
    setLocationError('');

    // Auto-start if task is TODO
    const status = taskInfo?.status || rowData?.status;
    if (status === 'TODO' && config.startScript) {
      FlexService.executeScript({
        scriptName: config.startScript,
        context: { _payload: { countTaskId } },
      }).then((res) => {
        if (res.success && res.context?.result) {
          setTaskInfo((prev) => ({ ...prev, ...res.context.result, status: 'COUNTING' }));
        }
        // Load entries after starting
        reloadEntries();
      }).catch((err) => console.error('Failed to start task:', err));
    } else {
      // Task already started — load existing entries
      reloadEntries();
    }
  }, [locationScan, expectedLocationId, taskInfo, rowData, config.startScript, countTaskId, reloadEntries]);

  // Handle Enter key on location scan field
  const handleLocationKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLocationVerify();
    }
  }, [handleLocationVerify]);

  const handleFormChange = useCallback((field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  const validateForm = useCallback(() => {
    const errs = {};
    if (!formValues.itemId.trim()) errs.itemId = 'Item is required';
    if (!formValues.quantityCounted && formValues.quantityCounted !== 0) errs.quantityCounted = 'Quantity is required';
    if (!formValues.inventoryStatus) errs.inventoryStatus = 'Status is required';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formValues]);

  const handleAddEntry = useCallback(async () => {
    if (!validateForm()) return;

    setAddingEntry(true);
    try {
      const payload = {
        countTaskId,
        ...formValues,
        quantityCounted: Number(formValues.quantityCounted),
      };

      // Remove empty optional fields
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === null || payload[k] === undefined) delete payload[k];
      });

      const scriptName = editingEntry ? config.updateEntryScript : config.addEntryScript;
      if (editingEntry) payload.id = editingEntry.id;

      const res = await FlexService.executeScript({
        scriptName,
        context: { _payload: payload },
      });

      if (res.success) {
        // Reload entries
        await reloadEntries();
        clearForm();
      } else {
        setSubmitError(res.context?.error_message || res.errorMessage || 'Failed to save entry');
      }
    } catch (err) {
      setSubmitError(err.message || 'Failed to save entry');
    } finally {
      setAddingEntry(false);
    }
  }, [validateForm, countTaskId, formValues, editingEntry, config, reloadEntries]);

  const clearForm = useCallback(() => {
    setFormValues({
      palletId: '',
      caseId: '',
      itemId: '',
      quantityCounted: '',
      uom: '',
      inventoryStatus: '',
      lotCode: '',
      expirationDate: '',
      ownerId: '',
    });
    setFormErrors({});
    setEditingEntry(null);
    setSubmitError(null);
  }, []);

  const handleEditEntry = useCallback((entry) => {
    setEditingEntry(entry);
    setFormValues({
      palletId: entry.palletId || '',
      caseId: entry.caseId || '',
      itemId: entry.itemId || '',
      quantityCounted: entry.quantityCounted != null ? String(entry.quantityCounted) : '',
      uom: entry.uom || '',
      inventoryStatus: entry.inventoryStatus || 'AVAILABLE',
      lotCode: entry.lotCode || '',
      expirationDate: entry.expirationDate || '',
      ownerId: entry.ownerId || '',
    });
  }, []);

  const handleDeleteEntry = useCallback(async (entry) => {
    if (!config.deleteEntryScript) return;
    try {
      await FlexService.executeScript({
        scriptName: config.deleteEntryScript,
        context: { _payload: { id: entry.id } },
      });
      await reloadEntries();
    } catch (err) {
      console.error('Failed to delete entry:', err);
    }
  }, [config.deleteEntryScript, reloadEntries]);

  const handleSubmit = useCallback(async () => {
    if (!config.submitScript) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = { countTaskId };
      if (entries.length === 0 && confirmedEmpty) payload.confirmedEmpty = true;
      const res = await FlexService.executeScript({
        scriptName: config.submitScript,
        context: { _payload: payload },
      });
      // res.success only means the Command ran; a count the warehouse refused says so in context.success
      if (res.success && res.context?.success !== false) {
        if (onSave) onSave(res.context?.result);
      } else {
        setSubmitError(res.context?.error_message || res.errorMessage || 'Submit failed');
      }
    } catch (err) {
      setSubmitError(err.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  }, [config.submitScript, countTaskId, onSave, entries.length, confirmedEmpty]);

  const getStatusLabel = (val) => {
    const opt = statusOptions.find((o) => (o.value || o.optionValue || o.listValueValue) === val);
    return opt ? (opt.label || opt.description || opt.optionValue || val) : val;
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      bgcolor: colors.background,
      '& fieldset': { borderColor: colors.border },
      '&:hover fieldset': { borderColor: '#22c55e' },
      '&.Mui-focused fieldset': { borderColor: '#22c55e' },
    },
    '& .MuiInputBase-input': { color: colors.textPrimary },
    '& .MuiInputLabel-root': { color: colors.textMuted },
  };

  if (loading) {
    return (
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, bgcolor: colors.background, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={32} sx={{ color: '#22c55e' }} />
      </Box>
    );
  }

  const taskStatus = taskInfo?.status || rowData?.status || 'TODO';

  return (
    <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, bgcolor: colors.background, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
            Count Location: {expectedLocationId}
          </Typography>
          <Chip label={`Task: ${countTaskId}`} size="small" sx={{ bgcolor: colors.surfaceElevated, color: colors.textSecondary, fontSize: '0.7rem' }} />
          <Chip label={taskStatus} size="small" sx={{
            bgcolor: taskStatus === 'COUNTING' ? '#3b82f6' : taskStatus === 'SUBMITTED' ? '#f59e0b' : colors.surfaceElevated,
            color: '#fff', fontSize: '0.7rem',
          }} />
          {rowData?.areaId && (
            <Chip label={`Area: ${rowData.areaId}`} size="small" variant="outlined" sx={{ borderColor: colors.border, color: colors.textMuted, fontSize: '0.7rem' }} />
          )}
        </Box>
        <IconButton size="small" onClick={onCancel} sx={{ color: colors.textMuted }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>

        {/* Location Verification Gate */}
        {!locationVerified && (
          <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4, textAlign: 'center' }}>
            <QrCodeScannerIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 2 }} />
            <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>
              Verify Location
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted, mb: 3 }}>
              Scan or enter the location barcode to confirm you are at the correct location before counting.
            </Typography>
            <TextField
              fullWidth size="small" label="Scan Location"
              placeholder={`Expected: ${expectedLocationId}`}
              value={locationScan}
              onChange={(e) => { setLocationScan(e.target.value); setLocationError(''); }}
              onKeyDown={handleLocationKeyDown}
              autoFocus
              error={!!locationError}
              helperText={locationError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleLocationVerify} sx={{ color: '#22c55e' }}>
                      <CheckCircleIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2, ...inputSx }}
            />
            <Button
              variant="contained" onClick={handleLocationVerify}
              sx={{ bgcolor: '#22c55e', color: '#fff', textTransform: 'none', borderRadius: 0, '&:hover': { bgcolor: '#16a34a' } }}
            >
              Verify & Start Counting
            </Button>
          </Box>
        )}

        {/* Counting Interface (only after location verified) */}
        {locationVerified && (
          <>
            {/* Verified badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 1, py: 0.5, bgcolor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)' }}>
              <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600 }}>
                Location verified: {expectedLocationId}
              </Typography>
            </Box>

            {/* Entry Form */}
            <Box {...enterAdvanceProps(() => { if (!addingEntry) handleAddEntry(); })}
              sx={{ border: `1px solid ${colors.border}`, p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 1.5 }}>
                {editingEntry ? 'Edit Entry' : 'Add Count Entry'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <TextField
                  size="small" label="Pallet ID" value={formValues.palletId}
                  onChange={(e) => handleFormChange('palletId', e.target.value)}
                  sx={{ flex: '1 1 180px', ...inputSx }}
                />
                <TextField
                  size="small" label="Case ID" value={formValues.caseId}
                  onChange={(e) => handleFormChange('caseId', e.target.value)}
                  sx={{ flex: '1 1 180px', ...inputSx }}
                />
                <TextField
                  size="small" label="Item *" value={formValues.itemId}
                  onChange={(e) => handleFormChange('itemId', e.target.value)}
                  error={!!formErrors.itemId} helperText={formErrors.itemId}
                  sx={{ flex: '1 1 180px', ...inputSx }}
                />
                <TextField
                  size="small" label="Qty *" type="number" value={formValues.quantityCounted}
                  onChange={(e) => handleFormChange('quantityCounted', e.target.value)}
                  error={!!formErrors.quantityCounted} helperText={formErrors.quantityCounted}
                  sx={{ flex: '0 0 100px', ...inputSx }}
                />
                <TextField
                  size="small" label="UOM" value={formValues.uom}
                  onChange={(e) => handleFormChange('uom', e.target.value)}
                  placeholder="EACH"
                  sx={{ flex: '0 0 100px', ...inputSx }}
                />
                <FormControl size="small" sx={{ flex: '0 0 140px' }} error={!!formErrors.inventoryStatus}>
                  {/* labelId names the dropdown for screen readers (and getByRole) */}
                  <InputLabel id="count-entry-status-label" sx={{ color: colors.textMuted }}>Status *</InputLabel>
                  <Select
                    labelId="count-entry-status-label"
                    value={formValues.inventoryStatus} label="Status *"
                    onChange={(e) => handleFormChange('inventoryStatus', e.target.value)}
                    sx={{ borderRadius: 0, bgcolor: colors.background, color: colors.textPrimary, '& fieldset': { borderColor: colors.border } }}
                  >
                    {statusOptions.length > 0
                      ? statusOptions.map((o) => (
                          <MenuItem key={o.value || o.optionValue || o.listValueValue} value={o.value || o.optionValue || o.listValueValue}>
                            {o.label || o.description || o.optionValue || o.value}
                          </MenuItem>
                        ))
                      : []
                    }
                  </Select>
                </FormControl>
                <TextField
                  size="small" label="Lot Code" value={formValues.lotCode}
                  onChange={(e) => handleFormChange('lotCode', e.target.value)}
                  sx={{ flex: '1 1 140px', ...inputSx }}
                />
                <TextField
                  size="small" label="Expiry" type="date" value={formValues.expirationDate}
                  onChange={(e) => handleFormChange('expirationDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: '0 0 160px', ...inputSx }}
                />
                <TextField
                  size="small" label="Owner" value={formValues.ownerId}
                  onChange={(e) => handleFormChange('ownerId', e.target.value)}
                  sx={{ flex: '1 1 140px', ...inputSx }}
                />
              </Box>
              {submitError && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <ErrorIcon sx={{ color: '#ef4444', fontSize: 14 }} />
                  <Typography variant="caption" sx={{ color: '#ef4444' }}>{submitError}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                {editingEntry && (
                  <Button size="small" onClick={clearForm} sx={{ color: colors.textMuted, textTransform: 'none', borderRadius: 0 }}>
                    Cancel Edit
                  </Button>
                )}
                <Button
                  size="small" variant="contained"
                  startIcon={addingEntry ? <CircularProgress size={14} /> : <AddIcon />}
                  onClick={handleAddEntry} disabled={addingEntry}
                  sx={{ bgcolor: '#22c55e', color: '#fff', textTransform: 'none', borderRadius: 0, '&:hover': { bgcolor: '#16a34a' } }}
                >
                  {editingEntry ? 'Update Entry' : (entries.length > 0 ? 'Add Another' : 'Add Entry')}
                </Button>
              </Box>
            </Box>

            {/* Entries Grid */}
            <Box sx={{ border: `1px solid ${colors.border}`, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.75, bgcolor: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
                <Typography variant="subtitle2" sx={{ color: colors.textSecondary }}>
                  Entries ({entries.length})
                </Typography>
              </Box>

              {/* Grid header */}
              <Box sx={{ display: 'flex', px: 1.5, py: 0.5, bgcolor: colors.surfaceElevated, borderBottom: `1px solid ${colors.border}` }}>
                {['#', 'Pallet', 'Case', 'Item', 'Qty', 'UOM', 'Owner', 'Status', 'Lot', 'Expiry', ''].map((h, i) => (
                  <Typography key={i} variant="caption" sx={{
                    color: colors.textMuted, fontWeight: 600,
                    flex: i === 10 ? '0 0 60px' : i === 0 ? '0 0 30px' : i === 4 ? '0 0 60px' : i === 5 ? '0 0 60px' : '1 1 0',
                    textAlign: i === 4 ? 'right' : 'left',
                  }}>
                    {h}
                  </Typography>
                ))}
              </Box>

              {/* Grid rows */}
              {entries.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: colors.textMuted, mb: 1 }}>
                    No entries yet. Add items counted at this location.
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={confirmedEmpty}
                        onChange={(e) => setConfirmedEmpty(e.target.checked)}
                        sx={{ color: colors.textMuted, '&.Mui-checked': { color: '#22c55e' } }}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        This location is verified empty -- submit a zero count
                      </Typography>
                    }
                  />
                </Box>
              ) : (
                entries.map((entry, idx) => (
                  <Box key={entry.id || idx} sx={{
                    display: 'flex', px: 1.5, py: 0.75, alignItems: 'center',
                    borderBottom: `1px solid ${colors.border}`,
                    bgcolor: editingEntry?.id === entry.id ? 'rgba(34,197,94,0.05)' : 'transparent',
                    '&:hover': { bgcolor: colors.surfaceElevated },
                  }}>
                    <Typography variant="body2" sx={{ flex: '0 0 30px', color: colors.textMuted, fontSize: '0.75rem' }}>{idx + 1}</Typography>
                    <Typography variant="body2" sx={{ flex: '1 1 0', color: colors.textPrimary, fontSize: '0.8rem' }}>{entry.palletId || '-'}</Typography>
                    <Typography variant="body2" sx={{ flex: '1 1 0', color: colors.textPrimary, fontSize: '0.8rem' }}>{entry.caseId || '-'}</Typography>
                    <Typography variant="body2" sx={{ flex: '1 1 0', color: colors.textPrimary, fontSize: '0.8rem' }}>{entry.itemId}</Typography>
                    <Typography variant="body2" sx={{ flex: '0 0 60px', color: colors.textPrimary, fontSize: '0.8rem', textAlign: 'right', fontWeight: 600 }}>{entry.quantityCounted}</Typography>
                    <Typography variant="body2" sx={{ flex: '0 0 60px', color: colors.textSecondary, fontSize: '0.8rem' }}>{entry.uom || 'EACH'}</Typography>
                    <Typography variant="body2" sx={{ flex: '1 1 0', color: colors.textSecondary, fontSize: '0.8rem' }}>{entry.ownerId || '-'}</Typography>
                    <Typography variant="body2" sx={{ flex: '1 1 0', color: colors.textSecondary, fontSize: '0.8rem' }}>{entry.inventoryStatus}</Typography>
                    <Typography variant="body2" sx={{ flex: '1 1 0', color: colors.textSecondary, fontSize: '0.8rem' }}>{entry.lotCode || '-'}</Typography>
                    <Typography variant="body2" sx={{ flex: '1 1 0', color: colors.textSecondary, fontSize: '0.8rem' }}>
                      {entry.expirationDate ? formatDateOnly(entry.expirationDate) : '-'}
                    </Typography>
                    <Box sx={{ flex: '0 0 60px', display: 'flex', gap: 0.25 }}>
                      <IconButton size="small" onClick={() => handleEditEntry(entry)} sx={{ color: colors.textMuted, p: 0.25 }}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteEntry(entry)} sx={{ color: '#ef4444', p: 0.25 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Box>
                ))
              )}
            </Box>

          </>
        )}
      </Box>

      {/* Footer */}
      {locationVerified && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, borderTop: `1px solid ${colors.border}`, bgcolor: colors.surface }}>
          <Button size="small" onClick={onCancel} sx={{ color: colors.textMuted, textTransform: 'none', borderRadius: 0 }}>
            Close
          </Button>
          <Button
            size="small" variant="contained"
            startIcon={submitting ? <CircularProgress size={14} /> : <SendIcon />}
            onClick={handleSubmit} disabled={submitting || (entries.length === 0 && !confirmedEmpty)}
            sx={{ bgcolor: '#3b82f6', color: '#fff', textTransform: 'none', borderRadius: 0, '&:hover': { bgcolor: '#2563eb' } }}
          >
            Submit for Review
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CountEntryPanel;
